import React, { useState } from 'react';
import { getClubBannerPublicUrl, DEFAULT_CLUB_BANNER_URL } from '../../../services/clubBannerService';
import ClubLogo from '../ClubLogo';
import RankMovementLabel from '../../leaderboard/RankMovementLabel';
import EloChange from '../../common/EloChange';

function levelsLadderLabel(levelsLadder) {
  if (levelsLadder === 'levels_female') return "Women's";
  if (levelsLadder === 'levels_male') return "Men's";
  return 'Doubles';
}

function levelsLadderShortLabel(levelsLadder) {
  const full = levelsLadderLabel(levelsLadder);
  return full === 'Doubles' ? full : `${full} doubles`;
}

/**
 * One stat column in the dark ranking strip inside the hero.
 * Text colours are fixed white/light because the strip always sits on a dark background.
 */
function HeroStatCell({ label, rank, rankDelta, elo, eloDelta, value, loading, onClick }) {
  const isRankCell = rank !== undefined || elo !== undefined;

  const body = loading ? (
    <div className="mt-2 space-y-2">
      <div className="h-6 w-12 mx-auto rounded bg-white/20 animate-pulse" />
      {isRankCell && <div className="h-3 w-14 mx-auto rounded bg-white/15 animate-pulse" />}
    </div>
  ) : isRankCell ? (
    <>
      <div className="mt-1.5 flex items-baseline justify-center gap-1 flex-wrap">
        <span className="text-xl sm:text-2xl font-bold tabular-nums text-white leading-none">
          {rank != null ? `#${rank}` : '—'}
        </span>
        {rankDelta != null && rankDelta !== 0 ? (
          <RankMovementLabel rankDelta={rankDelta} size="xs" />
        ) : null}
      </div>
      {elo != null ? (
        <div className="mt-1 flex items-center justify-center gap-1 flex-wrap">
          <span className="text-[11px] font-medium tabular-nums text-white/70">
            {elo} Elo
          </span>
          {eloDelta != null && eloDelta !== 0 ? (
            <EloChange change={eloDelta} size="xs" showSign />
          ) : null}
        </div>
      ) : null}
    </>
  ) : (
    <span className="mt-1.5 block text-xl sm:text-2xl font-bold tabular-nums text-white leading-none text-center">
      {value ?? '—'}
    </span>
  );

  const cellClass = 'min-w-0 flex flex-col text-center px-2.5 py-3 sm:px-4 sm:py-3.5';

  const labelEl = (
    <p className="text-[9px] sm:text-[10px] font-semibold uppercase tracking-wider text-white/60 truncate leading-tight">
      {label}
    </p>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${cellClass} hover:bg-white/10 active:bg-white/15 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/50`}
        aria-label={`${label} — view leaderboard`}
      >
        {labelEl}
        {body}
      </button>
    );
  }

  return <div className={cellClass}>{labelEl}{body}</div>;
}

/**
 * Rich hero for the My Club overview tab.
 *
 * Merges:
 * - Club banner image as full-bleed background
 * - Club logo, name, location, description
 * - Meta pills: Season | Members | Sessions
 * - Your rankings strip (Mixed | Doubles | Your sessions)
 *
 * @param {Object}   club
 * @param {Object}   [clubStats]         - { clubPlayersCount, finishedSessions }
 * @param {Object}   [selectedSeason]    - { name }
 * @param {Object}   recap               - { mixed, levels, levelsLadder }
 * @param {boolean}  recapLoading
 * @param {number|null} sessionsPlayed
 * @param {Function} onViewLeaderboard   - Tapping rank stat cells navigates to leaderboards
 */
export function ClubOverviewHero({
  club,
  clubStats,
  selectedSeason,
  recap,
  recapLoading,
  sessionsPlayed,
  onViewLeaderboard,
}) {
  const [bannerError, setBannerError] = useState(false);
  const [bannerLoaded, setBannerLoaded] = useState(false);

  const customUrl = club?.banner_image_url ? getClubBannerPublicUrl(club.banner_image_url) : null;
  const bannerUrl = customUrl && !bannerError ? customUrl : DEFAULT_CLUB_BANNER_URL;

  const mixed = recap?.mixed;
  const levels = recap?.levels;
  const levelsLadder = recap?.levelsLadder;
  const levelsLabel = levelsLadderShortLabel(levelsLadder);

  return (
    <section className="rounded-2xl overflow-hidden shadow-brand-lg ring-1 ring-white/[0.06]">
      {/* Banner image with gradient overlay */}
      <div className="relative">
        <div className="relative h-28 sm:h-36 overflow-hidden">
          <img
            src={bannerUrl}
            alt={`${club?.name ?? 'Club'} banner`}
            loading="lazy"
            onError={() => setBannerError(true)}
            onLoad={() => setBannerLoaded(true)}
            className={`w-full h-full object-cover transition-opacity duration-300 ${bannerLoaded ? 'opacity-100' : 'opacity-0'}`}
          />
          {!bannerLoaded && (
            <div className="absolute inset-0 bg-gradient-to-br from-primary/30 via-accent/20 to-info/30 animate-pulse" />
          )}
          {/* Gradient overlay — dark at bottom for text contrast */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0e1624]/95 via-[#0e1624]/40 to-transparent" />
        </div>

        {/* Club identity — overlaid on banner */}
        <div className="absolute bottom-0 left-0 right-0 px-4 pb-3 sm:px-5 sm:pb-4">
          <div className="flex items-end gap-3">
            {(club?.logo_image_url) && (
              <ClubLogo
                logoImageUrl={club.logo_image_url}
                clubName={club?.name ?? ''}
                size="md"
                className="border-2 border-white/30 shadow-md shrink-0 mb-0.5"
              />
            )}
            <div className="min-w-0 flex-1">
              <h2 className="text-lg sm:text-xl font-bold text-white leading-tight truncate drop-shadow-md">
                {club?.name ?? ''}
              </h2>
              {club?.location && (
                <p className="text-sm text-white/80 drop-shadow-sm leading-snug mt-0.5 truncate">
                  {club.location}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Dark body — description + meta pills + rankings strip + actions */}
      <div className="bg-[#0e1624]">
        {/* Description */}
        {club?.description && (
          <p className="px-4 pt-3 pb-0 sm:px-5 text-[0.8125rem] sm:text-sm text-white/70 leading-relaxed line-clamp-2">
            {club.description}
          </p>
        )}

        {/* Meta pills */}
        <div className="flex flex-wrap gap-2 px-4 pt-3 sm:px-5">
          {selectedSeason?.name && (
            <span className="inline-flex items-center gap-1 text-[0.65rem] font-semibold px-2.5 py-1 rounded-full bg-white/[0.08] border border-white/[0.12] text-white/80">
              🗓 {selectedSeason.name}
            </span>
          )}
          {clubStats?.clubPlayersCount != null && (
            <span className="inline-flex items-center gap-1 text-[0.65rem] font-semibold px-2.5 py-1 rounded-full bg-white/[0.08] border border-white/[0.12] text-white/80">
              👥 {clubStats.clubPlayersCount} members
            </span>
          )}
          {clubStats?.finishedSessions != null && (
            <span className="inline-flex items-center gap-1 text-[0.65rem] font-semibold px-2.5 py-1 rounded-full bg-white/[0.08] border border-white/[0.12] text-white/80">
              🏸 {clubStats.finishedSessions} sessions
            </span>
          )}
        </div>

        {/* YOUR RANKINGS divider label */}
        <p className="px-4 pt-4 pb-1 sm:px-5 text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-white/40">
          Your rankings
        </p>

        {/* Rankings strip */}
        <div className="grid grid-cols-3 divide-x divide-white/[0.1] border-t border-white/[0.1] pb-1">
          <HeroStatCell
            label="Mixed"
            rank={mixed?.rank ?? null}
            rankDelta={mixed?.rank_delta ?? null}
            elo={mixed?.elo_rating ?? null}
            eloDelta={mixed?.elo_delta ?? null}
            loading={recapLoading}
            onClick={onViewLeaderboard}
          />
          <HeroStatCell
            label={levelsLabel}
            rank={levels?.rank ?? null}
            rankDelta={levels?.rank_delta ?? null}
            elo={levels?.elo_rating ?? null}
            eloDelta={levels?.elo_delta ?? null}
            loading={recapLoading}
            onClick={onViewLeaderboard}
          />
          <HeroStatCell
            label="Your sessions"
            value={sessionsPlayed}
            loading={recapLoading && sessionsPlayed === null}
          />
        </div>
      </div>
    </section>
  );
}
